import { useEffect, useMemo, useRef, useState } from "react";
import {
  createCommercialInvoiceSignedUrl,
  fetchCommercialInvoiceBlob,
} from "../../../utils/commercialInvoiceUtils";
import { getFileType } from "../../../utils/containerDocumentsUtils";
import {
  ExcelPreviewData,
  parseExcelBlobForPreview,
} from "../../../utils/excelPreviewUtils";

const EXCEL_PREVIEW_MAX_ROWS = 80;

export const useCommercialInvoicePreview = (
  commercialInvoiceFile: string | null | undefined,
  bookingId?: number
) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [excelPreview, setExcelPreview] = useState<ExcelPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const invoicePaths = useMemo(
    () =>
      commercialInvoiceFile
        ?.split(",")
        .map((path) => path.trim())
        .filter(Boolean) ?? [],
    [commercialInvoiceFile]
  );

  const primaryPath = invoicePaths[0] ?? null;
  const primaryFileName = primaryPath?.split("/").pop() ?? "Commercial invoice";
  const fileType = getFileType(primaryFileName);
  const isExcel = fileType.type === "excel";
  const canInlinePreview =
    fileType.type === "pdf" || fileType.type === "image" || isExcel;

  useEffect(() => {
    if (!primaryPath) {
      setPreviewUrl(null);
      setExcelPreview(null);
      return;
    }

    let active = true;
    setLoading(true);
    setPreviewUrl(null);
    setExcelPreview(null);

    const loadPreview = isExcel
      ? fetchCommercialInvoiceBlob(primaryPath, bookingId).then((blob) =>
          parseExcelBlobForPreview(blob, EXCEL_PREVIEW_MAX_ROWS)
        )
      : createCommercialInvoiceSignedUrl(primaryPath, 3600, bookingId);

    loadPreview
      .then((result) => {
        if (!active) {
          if (typeof result === "string" && result) URL.revokeObjectURL(result);
          return;
        }
        if (isExcel) {
          setExcelPreview(result as ExcelPreviewData);
          return;
        }
        const url = result as string | null;
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (active) {
          setPreviewUrl(null);
          setExcelPreview(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [primaryPath, bookingId, isExcel]);

  return {
    invoicePaths,
    primaryPath,
    primaryFileName,
    fileType,
    isExcel,
    canInlinePreview,
    previewUrl,
    excelPreview,
    loading,
  };
};
